import { css } from "@emotion/react";
import { ExtractSwarovskiPageData, PlpSearchResponse, ProductListingPageData } from "@swo/storefront";
import cn from "classnames";
import React from "react";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { Spacer, SpacerCustom } from "../../../components/_shared-components/arrangement/Space";
import { LoadingStateWithIdleOps } from "../../../domain/data-layer/loading-state-with-idle";
import { useGeneralTranslationsContext } from "../../../global/hooks/use-general-translations-context";
import { ModalLoadingSpinner } from "../../../routes/modal-loading-spinner";
import { ProductListingPageCategorySelector } from "../ProductListingPageCategorySelector/ProductListingPageCategorySelector";
import { ProductListingPageDataLayout } from "../ProductListingPageDataLayout";
import { useStorefrontData } from "../io/use-storefront-data";
import { PlpSitemapLinker } from "../sitemap/plp-sitemap";
import { PlpFacetedSearchSubPage, PlpRootSubpage } from "../sitemap/use-plp-subpage";
import { useProductImpressions } from "../useProductImpressions";
import { ErrorView } from "./ErrorView";
import { buttonLabel, errorMessage } from "./translations";

export type PlpRootLevelProps = {
  hasMetadata: boolean;
  sitemapLinker: PlpSitemapLinker;
  subpage: PlpFacetedSearchSubPage | PlpRootSubpage;
} & Omit<ExtractSwarovskiPageData<ProductListingPageData>, "metadata">;

export const PlpRootLevel: React.FC<PlpRootLevelProps> = ({
  sitemapLinker,
  page,
  segment,
  subpage: subpage_,
  data,
}) => {
  const t = useGeneralTranslationsContext();

  const [facetsQuery, storefrontResponse, storefrontResponseWithStaleness] = useStorefrontData({
    page,
    segment,
    subpage: subpage_,
  });

  useProductImpressions(facetsQuery, storefrontResponse, {
    category: page.categoryKey,
    segment: segment.key,
  });

  const elements = data.productTypes.map(c => ({
    ...c,
    className: c.id === page.categoryId ? "active" : undefined,
    key: c.id,
  }));

  const isLoading = LoadingStateWithIdleOps.toLoadingState(storefrontResponse).__tag === "loading";

  const categoryLabel = t("categoryLabel");

  return (
    <>
      {isLoading && <ModalLoadingSpinner />}

      <ContentContainer
        className={cn("expand", {
          loading: isLoading,
        })}
        attachStyles={css`
          overflow: hidden;

          &.loading {
            min-height: 100vh;
          }
        `}
      >
        <Spacer size={"md"} />

        <ProductListingPageCategorySelector label={categoryLabel} elements={elements} />

        <SpacerCustom mobile={10} desktop={40} />

        <ProductListingPageDataLayout
          isRefetching={isLoading}
          storefrontResponseWithStalenessLS={LoadingStateWithIdleOps.toLoadingState(storefrontResponseWithStaleness)}
          sitemapLinker={sitemapLinker}
          colorsCssMap={data.colorsCssMap}
          isSparePartsPlp={data.isSparePartsPlp}
          tiles={data.tiles}
          label={categoryLabel}
          page={page}
          segment={segment}
        />

        {storefrontResponseWithStaleness.__tag === "error" && (
          <ErrorView pathname={location.pathname} buttonLabel={buttonLabel} errorMessage={errorMessage} />
        )}
      </ContentContainer>
    </>
  );
};
